import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Calendar, ArrowUpRight, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar as CalendarComponent } from "../ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { format } from "date-fns";
import axios from "axios";

// Fall back to a same-origin path so a missing build-time env var degrades to a
// relative request instead of posting to the literal URL "undefined/api".
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL.replace(/\/$/, "")}/api`;

// Mirrors the server-side caps in backend/server.py. Enforced there too - this
// is only to fail fast in the UI rather than round-trip a 422.
const LIMITS = {
  name: 100,
  email: 254,
  phone: 10,
  message: 5000,
};

// North American Numbering Plan: 10 digits, area code and exchange both 2-9.
const US_PHONE = /^[2-9]\d{2}[2-9]\d{6}$/;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    preferredDate: null,
    preferredTime: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Digits only, capped at 10 - the field simply won't accept anything else.
  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: digits }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData((prev) => ({ ...prev, preferredDate: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Phone is optional, but if given it must be a real US number. Checked here
    // as well as server-side so the visitor sees it without a round-trip.
    if (formData.phone && !US_PHONE.test(formData.phone)) {
      setError(
        "Please enter a valid 10-digit US phone number, e.g. 8186051215."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        preferredDate: formData.preferredDate ? format(formData.preferredDate, "yyyy-MM-dd") : null,
      });
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
        preferredDate: null,
        preferredTime: "",
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(
          "You've sent several messages recently. Please wait a few minutes or call us directly at (818) 605-1215."
        );
      } else if (status === 422) {
        setError("Please check your details — one or more entries look invalid.");
      } else {
        setError("Something went wrong. Please try again or call us directly.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-40 bg-[#F9F8F6] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Editorial background numeral */}
      <div className="absolute -left-24 -bottom-24 font-heading text-[24rem] font-light italic text-[#EBE6DD]/60 leading-none pointer-events-none select-none hidden lg:block">
        05
      </div>

      <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px bg-[#C85A43] w-12" />
              <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                Get in Touch
              </p>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-[#1C1917]">
              Let's start a{" "}
              <span className="italic font-light text-[#C85A43]">
                conversation.
              </span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pt-16"
          >
            <p className="text-lg text-[#3A3634] leading-relaxed max-w-xl">
              Book a complimentary consultation. Tell us a bit about you, and
              we'll follow up within one business day to schedule a time that
              works.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 space-y-10"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#78716C] mb-4">
                Direct Line
              </p>
              <a
                href="tel:+18186051215"
                className="font-heading text-3xl md:text-4xl font-light text-[#1C1917] hover:text-[#C85A43] transition-colors inline-flex items-center gap-3 group"
                data-testid="contact-phone"
              >
                (818) 605-1215
                <ArrowUpRight className="w-6 h-6 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
              </a>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#78716C] mb-4">
                Email
              </p>
              <a
                href="mailto:info@marapyancpa.com"
                className="font-heading text-2xl md:text-3xl font-light text-[#1C1917] hover:text-[#C85A43] transition-colors inline-flex items-center gap-3 group"
                data-testid="contact-email"
              >
                info@marapyancpa.com
                <ArrowUpRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
              </a>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-6 border-t border-[#EBE6DD]">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#78716C] mb-3 flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  Mailing Address
                </p>
                <p className="text-[#1C1917] text-sm leading-relaxed">
                  221 N Third St, #393<br />
                  Burbank, CA 91502
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#78716C] mb-3 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Hours
                </p>
                <p className="text-[#1C1917] text-sm leading-relaxed">
                  Mon – Fri · 9AM – 6PM PT<br />
                  Sat · By Appointment
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            {isSubmitted ? (
              <div className="bg-white border border-[#EBE6DD] rounded-sm p-12 text-center">
                <div className="w-16 h-16 bg-[#C85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#C85A43]" />
                </div>
                <h3 className="font-heading text-3xl font-semibold text-[#1C1917] mb-3">
                  Thank you.
                </h3>
                <p className="text-[#78716C] text-base mb-8">
                  We've received your message and will get back to you within
                  one business day.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-[#EBE6DD] hover:bg-[#EBE6DD] rounded-full px-6"
                  data-testid="submit-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-[#EBE6DD] rounded-sm p-8 md:p-12"
                data-testid="contact-form"
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm mb-6">
                    {error}
                  </div>
                )}

                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        maxLength={LIMITS.name}
                        autoComplete="name"
                        className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base"
                        placeholder="John Smith"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        maxLength={LIMITS.email}
                        autoComplete="email"
                        className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        maxLength={LIMITS.phone}
                        autoComplete="tel"
                        className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base"
                        placeholder="5551234567"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Service
                      </Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => handleSelectChange("service", value)}
                      >
                        <SelectTrigger
                          className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base"
                          data-testid="select-service"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tax-prep">Tax Preparation</SelectItem>
                          <SelectItem value="tax-planning">Tax Planning</SelectItem>
                          <SelectItem value="bookkeeping">Bookkeeping</SelectItem>
                          <SelectItem value="accounting">Accounting</SelectItem>
                          <SelectItem value="consulting">Business Consulting</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Preferred Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            className="w-full justify-start text-left font-normal bg-transparent border-0 border-b border-[#EBE6DD] hover:bg-transparent hover:border-[#C85A43] rounded-none px-0 h-10"
                            data-testid="date-picker-trigger"
                          >
                            <Calendar className="mr-2 h-4 w-4 text-[#78716C]" />
                            {formData.preferredDate ? (
                              format(formData.preferredDate, "PPP")
                            ) : (
                              <span className="text-[#78716C]">Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={formData.preferredDate}
                            onSelect={handleDateSelect}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferredTime" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                        Preferred Time
                      </Label>
                      <Select
                        value={formData.preferredTime}
                        onValueChange={(value) => handleSelectChange("preferredTime", value)}
                      >
                        <SelectTrigger
                          className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base"
                          data-testid="select-time"
                        >
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="9am">9:00 AM</SelectItem>
                          <SelectItem value="10am">10:00 AM</SelectItem>
                          <SelectItem value="11am">11:00 AM</SelectItem>
                          <SelectItem value="1pm">1:00 PM</SelectItem>
                          <SelectItem value="2pm">2:00 PM</SelectItem>
                          <SelectItem value="3pm">3:00 PM</SelectItem>
                          <SelectItem value="4pm">4:00 PM</SelectItem>
                          <SelectItem value="5pm">5:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={LIMITS.message}
                      className="bg-transparent border-0 border-b border-[#EBE6DD] focus:border-[#C85A43] rounded-none px-0 focus:ring-0 text-base resize-none"
                      placeholder="Tell us about your needs..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group inline-flex items-center gap-3 bg-[#1C1917] hover:bg-[#C85A43] text-white rounded-full pl-7 pr-2 py-2 text-sm font-medium tracking-wide transition-all duration-500 mt-4"
                    data-testid="submit-btn"
                  >
                    <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                    <span className="w-10 h-10 rounded-full bg-[#C85A43] group-hover:bg-white flex items-center justify-center transition-all duration-500">
                      <ArrowUpRight className="w-4 h-4 text-white group-hover:text-[#C85A43] transition-colors duration-500" />
                    </span>
                  </button>

                  <p className="text-xs text-[#78716C]">
                    By submitting, you agree to our privacy policy.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
